import _ from "lodash";
import { queryWaterProvider } from "./api";
import { FilterMetadataApiWithCountReturn } from "./types";
import { QUERY_URL_AND } from "./constants";
import { FilterMetadataWithDefaultSelections, SearchParams } from "../ProviderFilter";
import { constructFacetFieldValues, getFacetFieldWithMetadata } from "./fetchProviderFilterMetadataWithCount";
import { filterProvidersBySearchParams, getFieldQueryWithSearchParamsAndMetadata } from "./fetchProvidersByFilters";
import { getProviders } from "./fetchProviders";

export const fetchProviderSelectionMetadataWithCount = async (
    metadata: FilterMetadataApiWithCountReturn,
    searchParams: SearchParams,
    options?: RequestInit
): Promise<FilterMetadataApiWithCountReturn> => {
    // empty search params
    const isEmptySearchParams = !_.flattenDeep(_.values(searchParams)).filter(d => d != null && d.trim() !== "")
        ?.length;

    const facetFieldQuery = getFacetFieldWithMetadata(metadata);
    const fieldQuery = getFieldQueryWithSearchParamsAndMetadata(metadata, searchParams);
    if (!facetFieldQuery) return undefined;

    // const fieldQuery = `fq=pure_strings_connection_size_ct: (NOT "bulk")`;

    let facetFields: Record<string, (string | number)[]> = {};
    const providers = getProviders();
    if (providers?.length > 0) {
        const _providers = filterProvidersBySearchParams(providers, searchParams);
        facetFields = constructFacetFieldValues(_providers);
    } else {
        const response = await queryWaterProvider(
            _.join(
                [
                    "q=*:*",
                    "rows=0",
                    "facet=on",
                    //fieldQuery,
                    fieldQuery ? `fq=${fieldQuery ?? ""}` : "",
                    facetFieldQuery
                ],
                QUERY_URL_AND
            ),
            options
        );
        facetFields = response?.facet_counts?.facet_fields ?? {};
    }

    const getNewMetadataState = (
        oldState: FilterMetadataWithDefaultSelections,
        newState: FilterMetadataWithDefaultSelections
    ): FilterMetadataWithDefaultSelections => {
        const same =
            oldState?.defaults === newState?.defaults &&
            oldState?.label === newState?.label &&
            (oldState?.options === newState?.options ||
                (oldState?.options?.length === newState?.options?.length &&
                    // find an old option and new state is not the same
                    !oldState?.options?.find((d, index) => d !== newState?.[index])));

        return same ? oldState : newState;
    };

    const newMetadata = _.toPairs(metadata)
        .map(([metadataKey, metadataState]) => {
            const { options, defaults, label } = metadataState;
            const field = _.uniq(
                (options ?? []).map(({ access }) => access).filter(d => d != null && d.toString().trim() !== "")
            )?.[0];

            if (field == null) return undefined;

            const metadataValue: FilterMetadataWithDefaultSelections = {
                defaults,
                label,
                options: options.map(d => {
                    if (isEmptySearchParams) {
                        if (d.disabled === undefined && d.filteredAmount === undefined) return d;

                        return {
                            ...d,
                            disabled: undefined,
                            filteredAmount: undefined
                        };
                    }

                    const disabled = true;
                    const filteredAmount = 0;
                    if (disabled === d.disabled && filteredAmount === d.filteredAmount) return d;

                    return {
                        ...d,
                        disabled,
                        filteredAmount
                    };
                })
            };

            if (!facetFields[field]) return [metadataKey, getNewMetadataState(metadataState, metadataValue)];

            const keyToAmount = _.chunk(facetFields[field] ?? [], 2).reduce<Record<string, number>>(
                (prev, [key, value]) => {
                    prev[`${key}`] = parseFloat(value.toString());
                    return prev;
                },
                {}
            );

            const newOptions = options.map(d => {
                if (isEmptySearchParams) {
                    if (d.disabled === undefined && d.filteredAmount === undefined) return d;

                    return {
                        ...d,
                        disabled: undefined,
                        filteredAmount: undefined
                    };
                }

                const disabled = !(keyToAmount?.[d.value] > 0);
                const filteredAmount = keyToAmount?.[d.value] ?? 0;

                if (disabled === d.disabled && filteredAmount === d.filteredAmount) return d;

                return {
                    ...d,
                    disabled,
                    filteredAmount
                };
            });

            metadataValue.options = newOptions;

            return [metadataKey, getNewMetadataState(metadataState, metadataValue)];
        })
        .reduce((prev, cur) => {
            if (cur != null && cur?.[0] != null && cur?.[1] != null) prev[cur?.[0] as string] = cur?.[1];

            return prev;
        }, {});

    const same =
        _.uniq([..._.keys(newMetadata), ..._.keys(metadata)])?.length === _.keys(metadata)?.length &&
        !_.keys(metadata).find(metadataKey => newMetadata[metadataKey] !== metadata[metadataKey]);

    return same ? metadata : newMetadata;
};
